import type { AutoLaunchTicketRowView, AutoLaunchUpgradeRowView } from "../../game/auto-launch/view";
import type { GameSnapshot } from "../../game/runtime/types";
import type { OrbitForgeRuntime } from "../../game/runtime/runtime";
import { formatCompact } from "../../utils/math";
import { createGameIcon, createIcon } from "../icon";
import { effectChip, formatDuration, textSpan } from "./crate-display";

export function createAutoLaunchPanel(runtime: OrbitForgeRuntime): HTMLElement {
  const panel = document.createElement("section");
  panel.className = "of-panel of-auto-panel";
  panel.setAttribute("aria-label", "Auto Launch");

  let renderedStatusSignature = "";
  let renderedListSignature = "";
  let wasOpen = false;
  runtime.subscribe((snapshot) => {
    const isOpen = snapshot.activePanel === "auto";
    if (isOpen !== wasOpen) {
      panel.classList.toggle("is-open", isOpen);
      wasOpen = isOpen;
    }
    if (!isOpen) {
      return;
    }

    ensureAutoPanelShell(panel, runtime);

    const statusSignature = autoStatusSignature(snapshot);
    if (statusSignature !== renderedStatusSignature) {
      updateAutoStatus(panel, snapshot);
      renderedStatusSignature = statusSignature;
    }

    const listSignature = autoListSignature(snapshot);
    if (listSignature !== renderedListSignature) {
      updateAutoList(panel, snapshot, runtime);
      renderedListSignature = listSignature;
    }
  });

  return panel;
}

function autoStatusSignature(snapshot: GameSnapshot): string {
  return [
    snapshot.activePanel,
    snapshot.auto.active ? Math.ceil(snapshot.auto.activeRemainingSeconds) : 0,
    snapshot.auto.activeTicketSeconds ?? 0,
    snapshot.auto.active ? 1 : 0,
    snapshot.auto.autoExtendEnabled ? 1 : 0,
    snapshot.auto.pausedByStorm ? 1 : 0,
    snapshot.auto.storageCount,
    snapshot.auto.storageCap,
    snapshot.auto.nextAutoExtendTicketSeconds ?? 0,
    snapshot.auto.durationMultiplier.toFixed(2),
    snapshot.auto.ballsPerSecond.toFixed(1),
    snapshot.auto.prestigeDiscountPercent
  ].join("|");
}

function autoListSignature(snapshot: GameSnapshot): string {
  return [
    snapshot.activePanel,
    snapshot.save.stats.Credits.toFixed(0),
    snapshot.auto.active ? 1 : 0,
    snapshot.auto.storageCount,
    ...snapshot.auto.tickets.map((ticket) => `${ticket.seconds}:${ticket.count}:${ticket.price}:${ticket.locked ? 1 : 0}:${ticket.canBuy ? 1 : 0}:${ticket.canUse ? 1 : 0}`),
    ...snapshot.auto.upgrades.map((upgrade) => `${upgrade.kind}:${upgrade.level}:${upgrade.price}:${upgrade.locked ? 1 : 0}:${upgrade.canBuy ? 1 : 0}:${upgrade.nextEffect}`)
  ].join("|");
}

function ensureAutoPanelShell(panel: HTMLElement, runtime: OrbitForgeRuntime): void {
  if (panel.querySelector(".of-auto-list")) {
    return;
  }

  const title = document.createElement("h2");
  title.className = "of-panel-title";
  title.textContent = "AUTO";

  const header = document.createElement("div");
  header.className = "of-panel-header";
  header.append(title);

  const status = createStatusShell(runtime);
  const list = document.createElement("div");
  list.className = "of-auto-list";

  panel.replaceChildren(header, status, list);
}

function createStatusShell(runtime: OrbitForgeRuntime): HTMLElement {
  const status = document.createElement("article");
  status.className = "of-auto-status";

  const details = document.createElement("div");
  details.className = "of-auto-status-details";
  details.append(textSpan("", "of-auto-status-title"), textSpan("", "of-auto-status-subtitle"));

  const stats = document.createElement("div");
  stats.className = "of-auto-status-stats";

  const toggle = document.createElement("button");
  toggle.className = "of-auto-toggle";
  toggle.type = "button";
  toggle.append(
    textSpan("AUTO-EXTEND", "of-auto-toggle-label"),
    textSpan("", "of-auto-toggle-state")
  );
  toggle.addEventListener("click", () => runtime.dispatch({ type: "toggle-auto-extend" }));

  status.append(details, stats, toggle);
  return status;
}

function updateAutoStatus(panel: HTMLElement, snapshot: GameSnapshot): void {
  const status = panel.querySelector<HTMLElement>(".of-auto-status");
  const title = panel.querySelector<HTMLElement>(".of-auto-status-title");
  const subtitle = panel.querySelector<HTMLElement>(".of-auto-status-subtitle");
  const stats = panel.querySelector<HTMLElement>(".of-auto-status-stats");
  const toggle = panel.querySelector<HTMLButtonElement>(".of-auto-toggle");
  const toggleState = panel.querySelector<HTMLElement>(".of-auto-toggle-state");
  if (!status || !title || !subtitle || !stats || !toggle || !toggleState) {
    return;
  }

  status.classList.toggle("is-active", snapshot.auto.active);
  status.classList.toggle("is-paused", snapshot.auto.pausedByStorm);
  title.textContent = snapshot.auto.pausedByStorm ? "STORM PAUSE" : snapshot.auto.active ? "AUTO ACTIVE" : "TICKETS";
  subtitle.textContent = autoSubtitle(snapshot);
  const chips = [
    effectChip("TIME", snapshot.auto.active ? formatDuration(snapshot.auto.activeRemainingSeconds) : `x${snapshot.auto.durationMultiplier.toFixed(2)}`),
    effectChip("RATE", `${snapshot.auto.ballsPerSecond.toFixed(1)}/s`)
  ];
  if (snapshot.auto.prestigeDiscountPercent > 0) {
    chips.push(effectChip("PRESTIGE", `-${snapshot.auto.prestigeDiscountPercent}%`));
  }
  stats.replaceChildren(...chips);
  toggle.setAttribute("aria-pressed", snapshot.auto.autoExtendEnabled ? "true" : "false");
  toggleState.textContent = snapshot.auto.autoExtendEnabled ? "ON" : "OFF";
}

function updateAutoList(panel: HTMLElement, snapshot: GameSnapshot, runtime: OrbitForgeRuntime): void {
  const list = panel.querySelector<HTMLElement>(".of-auto-list");
  if (!list) {
    return;
  }

  const scrollTop = list.scrollTop;
  list.replaceChildren(
    ...snapshot.auto.tickets.map((ticket) => createTicketRow(ticket, runtime)),
    ...snapshot.auto.upgrades.map((upgrade) => createUpgradeRow(upgrade, runtime))
  );
  list.scrollTop = scrollTop;
}

function createTicketRow(ticket: AutoLaunchTicketRowView, runtime: OrbitForgeRuntime): HTMLElement {
  const row = autoRowBase(ticket.locked, ticket.canBuy || ticket.canUse);
  row.dataset.ticketSeconds = ticket.seconds.toString();

  const chips = ticket.locked ? [] : [effectChip("PRICE", `${formatCompact(ticket.price)} CR`)];
  const details = rowDetails(ticket.label, rowStateLabel(ticket.locked, ticket.locked ? `REQ LVL ${ticket.unlockLevel}` : `${ticket.count} STORED`), chips);

  const actions = document.createElement("div");
  actions.className = "of-auto-actions";
  actions.append(
    autoButton("BUY", ticket.canBuy, () => runtime.dispatch({ type: "buy-auto-ticket", seconds: ticket.seconds })),
    autoButton("USE", ticket.canUse, () => runtime.dispatch({ type: "use-auto-ticket", seconds: ticket.seconds }))
  );

  row.append(autoIcon(), details, actions);
  return row;
}

function createUpgradeRow(upgrade: AutoLaunchUpgradeRowView, runtime: OrbitForgeRuntime): HTMLElement {
  const row = autoRowBase(upgrade.locked, upgrade.canBuy);
  row.dataset.autoUpgrade = upgrade.kind;

  const chips = [
    effectChip("NEXT", upgrade.maxed ? "MAX" : upgrade.nextEffect)
  ];
  const details = rowDetails(upgrade.label, rowStateLabel(upgrade.locked, upgrade.locked ? `REQ LVL ${upgrade.unlockLevel}` : `LV ${upgrade.level}/${upgrade.maxLevel}`), chips);

  const actions = document.createElement("div");
  actions.className = "of-auto-actions";
  if (!upgrade.locked) {
    actions.append(textSpan(upgrade.maxed ? "MAX" : `${formatCompact(upgrade.price)} CR`, "of-auto-price"));
  }
  actions.append(autoButton(upgrade.maxed ? "MAX" : "BUY", upgrade.canBuy, () => runtime.dispatch({ type: "buy-auto-upgrade", kind: upgrade.kind })));

  row.append(autoIcon(), details, actions);
  return row;
}

function autoSubtitle(snapshot: GameSnapshot): string {
  if (snapshot.auto.pausedByStorm) {
    return "Auto resumes after Reactor Storm";
  }
  if (snapshot.auto.active) {
    return `${snapshot.auto.activeTicketSeconds}s ticket running`;
  }
  return `${snapshot.auto.storageCount}/${snapshot.auto.storageCap} stored${snapshot.auto.nextAutoExtendTicketSeconds ? ` | NEXT ${snapshot.auto.nextAutoExtendTicketSeconds}s` : ""}`;
}

function autoRowBase(locked: boolean, ready: boolean): HTMLElement {
  const row = document.createElement("article");
  row.className = "of-auto-row";
  row.classList.toggle("is-locked", locked);
  row.classList.toggle("is-ready", ready);
  return row;
}

function rowDetails(name: string, state: HTMLElement, chips: readonly HTMLElement[]): HTMLElement {
  const details = document.createElement("div");
  details.className = "of-auto-details";
  const nameLine = document.createElement("div");
  nameLine.className = "of-auto-name-line";
  nameLine.append(textSpan(name, "of-auto-name"), state);
  const effects = document.createElement("div");
  effects.className = "of-auto-effects";
  effects.append(...chips);
  details.append(nameLine, effects);
  return details;
}

function rowStateLabel(locked: boolean, text: string): HTMLElement {
  if (!locked) {
    return textSpan(text, "of-auto-level");
  }

  const label = document.createElement("span");
  label.className = "of-auto-level of-auto-requirement-line";
  const icon = createIcon("locked", "Locked");
  icon.classList.add("of-auto-lock-badge");
  icon.setAttribute("aria-hidden", "true");
  icon.removeAttribute("aria-label");
  icon.removeAttribute("role");
  label.append(icon, textSpan(text, "of-auto-requirement-text"));
  return label;
}

function autoButton(label: string, active: boolean, action: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "of-auto-button";
  button.type = "button";
  button.textContent = label;
  button.disabled = !active;
  button.setAttribute("aria-disabled", active ? "false" : "true");
  button.addEventListener("click", action);
  return button;
}

function autoIcon(): HTMLElement {
  const icon = document.createElement("span");
  icon.className = "of-auto-icon";
  icon.append(createGameIcon("autoLaunch", "Auto Launch"));
  return icon;
}
